import {createStyles} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {BaseChipList} from 'ui-library/chips/ChipList';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {InputChangeEvent} from 'util/Types';

import SharedMonomerConfigurationPageContainer, {
  MONOMER_CONFIGURATION_PURPOSE,
} from './SharedMonomerConfigurationPageContainer';

const TABLE_ROW_HEIGHT = 150;

interface HELMColumn extends ScrollableTableColumn {
  sortKey?: string;
}

// Minimal shape the shared page needs from a HELM library monomer. Callers supply their own
// concrete type (e.g. nucleotide `CoreLibMonomer` or `AaMonomerGraphQL`) via the callbacks below.
interface HELMLibraryMonomer {
  name: string;
  symbol: string;
}

export interface SharedHELMMonomerSelectionPageProps<
  TRow extends ScrollableTableRow,
  TMonomer extends HELMLibraryMonomer,
> {
  monomerDescriptor: string;
  libraryUrl: string;
  changeMethodUrl?: string;
  fetchHelmMonomers: () => Promise<Array<TMonomer>>;
  existingMonomerKeys: Set<string>;
  isExistingDataLoading?: boolean;
  isExistingDataError?: boolean;
  refetch: () => Promise<unknown>;
  getMonomerKey: (monomer: TMonomer) => string;
  columns: Array<HELMColumn>;
  mapMonomerToRowData: (monomer: TMonomer, classes: Record<string, string>) => TRow['data'];
  tableKey: string;
  onSubmit: (selectedKeys: Array<string>, helmMonomers: Array<TMonomer>) => Promise<number>;
  viewOnly?: boolean;
  dataTestAttrs: DataTestAttrs;
}

function pluralize(descriptor: string): string {
  return `${descriptor}s`;
}

function capitalize(descriptor: string): string {
  return descriptor.charAt(0).toUpperCase() + descriptor.slice(1);
}

export default function SharedHELMMonomerSelectionPage<
  TRow extends ScrollableTableRow,
  TMonomer extends HELMLibraryMonomer,
>({
  monomerDescriptor,
  libraryUrl,
  changeMethodUrl,
  fetchHelmMonomers,
  existingMonomerKeys,
  isExistingDataLoading,
  isExistingDataError,
  refetch,
  getMonomerKey,
  columns,
  mapMonomerToRowData,
  tableKey,
  onSubmit,
  viewOnly,
  dataTestAttrs,
}: SharedHELMMonomerSelectionPageProps<TRow, TMonomer>): JSX.Element {
  const {classes} = useStyles();
  const [helmMonomers, setHelmMonomers] = useState<Array<TMonomer>>([]);
  const [selectedKeys, setSelectedKeys] = useState<Array<string>>([]);
  const [query, setQuery] = useState<string>('');

  useEffect(() => {
    const doFetch = async (): Promise<void> => {
      const monomers = await fetchHelmMonomers();
      setHelmMonomers(monomers);
    };
    doFetch();
  }, [fetchHelmMonomers]);

  const purpose = viewOnly ? MONOMER_CONFIGURATION_PURPOSE.VIEW : MONOMER_CONFIGURATION_PURPOSE.CREATE;
  const libraryName = `${capitalize(monomerDescriptor)} library`;

  const selectedMonomerChips = selectedKeys.map((key) => {
    const name = helmMonomers.find((m) => getMonomerKey(m) === key)?.name;
    return (
      <Chip
        className={classes.monomerChip}
        key={key}
        size={ChipSize.SMALL}
        text={name}
        onRemove={() => {
          setSelectedKeys(selectedKeys.filter((k) => k !== key));
        }}
      />
    );
  });

  const handleRowsSelect = (rows: Array<TRow>, isSelected: boolean): void => {
    const modified = rows.map((row) => row.id);
    let newSelected;
    if (isSelected) {
      const toAdd = modified.filter((key) => !selectedKeys.includes(key) && !existingMonomerKeys.has(key));
      newSelected = selectedKeys.concat(toAdd);
    } else {
      newSelected = selectedKeys.filter((key) => !modified.includes(key));
    }
    setSelectedKeys(newSelected);
  };

  const handleRowClick = (row: TRow): void => {
    const key = row.id;
    if (existingMonomerKeys.has(key)) {
      return;
    }
    if (selectedKeys.includes(key)) {
      setSelectedKeys(selectedKeys.filter((k) => k !== key));
    } else {
      setSelectedKeys(selectedKeys.concat(key));
    }
  };

  const rows: Array<TRow> = helmMonomers
    .filter(
      (monomer) =>
        query === '' ||
        monomer.name.toLowerCase().includes(query.toLowerCase()) ||
        monomer.symbol.toLowerCase().includes(query.toLowerCase())
    )
    .map((monomer) => {
      const key = getMonomerKey(monomer);
      const alreadyExists = existingMonomerKeys.has(key);
      return {
        id: key,
        isSelected: selectedKeys.includes(key),
        displayAsSelected: alreadyExists,
        isSelectorDisabled: alreadyExists,
        tooltipText: alreadyExists ? `Already added to the ${monomerDescriptor} library` : undefined,
        data: mapMonomerToRowData(monomer, classes),
      } as TRow;
    });

  const handleSubmit = async (): Promise<void> => {
    try {
      LoadingScreen.show('Creating...');
      const count = await onSubmit(selectedKeys, helmMonomers);
      await refetch();
      notifications.success(`Imported (${count}) ${pluralize(monomerDescriptor)} into library`);
      navigateToURL(libraryUrl);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    } finally {
      LoadingScreen.hide();
    }
  };

  const copyMonomerSymbolsToClipboard = (): void => {
    const symbols: Array<string> = selectedKeys.map((key) => {
      const helmMonomer = helmMonomers.find((m) => getMonomerKey(m) === key);
      assert(helmMonomer != null, `Could not find HELM library monomer for ${key}`);
      return helmMonomer.symbol;
    });
    copyToClipboard(symbols.join(', '), {
      showToastOnSuccess: true,
      toastText: `Copied (${symbols.length}) ${monomerDescriptor} symbols to clipboard`,
    });
  };

  const monomerTable = (
    <SortableScrollableTable
      className={classes.table}
      canResizeColumns={true}
      columns={columns}
      getRowContentHeight={() => TABLE_ROW_HEIGHT}
      emptyTableText={`No ${pluralize(monomerDescriptor)} were found.`}
      getSortValue={(row: TRow, sortKey: string) => row.data[sortKey]}
      initialRows={rows}
      initialSort={[{sortKey: 'name'}]}
      tableKey={tableKey}
      calcLeftPadding={() => 40}
      matchParentHeight={true}
      rowSelectionMode={RowSelectionMode.MULTIPLE}
      onRowsSelect={handleRowsSelect}
      onRowClick={handleRowClick}
      isCompact={true}
    />
  );

  return (
    <div className={classes.pageContainer} {...getTestProps(dataTestAttrs)}>
      <SharedMonomerConfigurationPageContainer
        methodTitle="Search HELM Library"
        purpose={purpose}
        libraryName={libraryName}
        libraryUrl={libraryUrl}
        changeMethodUrl={viewOnly ? undefined : changeMethodUrl}
      />
      <div className={classes.selectedMonomersSection}>
        <span className={classes.boldHeader}>Selected monomers</span>
        <div className={classes.selectedMonomerChips}>
          {selectedKeys.length > 0 ? (
            <BaseChipList
              chips={selectedMonomerChips}
              chipSize={ChipSize.SMALL}
              maxChips={8}
              isVertical={false}
            />
          ) : (
            <span className={classes.noneSelectedText}>None selected</span>
          )}
        </div>
      </div>
      {isExistingDataError ? null : isExistingDataLoading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          <div className={classes.tableSection}>
            <div className={classes.tableHeaderSection}>
              <span className={classes.boldHeader}>HELM Library</span>
              <PlainSearchInput
                className={classes.searchBar}
                clearType={ClearType.X}
                onChange={(e: InputChangeEvent) => {
                  setQuery(e.target.value);
                }}
                onEscape={() => {
                  setQuery('');
                }}
                placeholder="Search by symbol or name"
                value={query}
              />
            </div>
            {helmMonomers.length > 0 ? monomerTable : <CenteredHorizontalThrobber />}
          </div>
          <div className={classes.submitBar}>
            <div className={classes.buttonsContainer}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                onClick={() => {
                  navigateToURL(libraryUrl);
                }}
                size={ButtonSize.MEDIUM}
                text="Cancel"
              />
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                text={
                  viewOnly
                    ? `Copy ${selectedKeys.length} symbols`
                    : `Add (${selectedKeys.length}) ${pluralize(monomerDescriptor)}`
                }
                size={ButtonSize.MEDIUM}
                disabled={selectedKeys.length === 0}
                onClick={viewOnly ? copyMonomerSymbolsToClipboard : handleSubmit}
                dataTestAttrs={{
                  ...dataTestAttrs,
                  element: 'addMonomersButton',
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
  },
  table: {
    height: '100%',
  },
  tableSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    borderTop: `1px solid ${theme.colors.gray[2]}`,
  },
  selectedMonomersSection: {
    flex: '0 0 auto',
    marginLeft: 40,
    maxHeight: 120,
    minHeight: 65,
  },
  selectedMonomerChips: {
    overflowY: 'auto' as const,
    maxHeight: 85,
    marginTop: 8,
  },
  monomerChip: {
    marginBottom: 8,
  },
  boldHeader: {
    fontSize: 13,
    lineHeight: 1.5,
    fontFamily: theme.fontFamily,
    fontWeight: 'bold' as const,
  },
  tableHeaderSection: {
    margin: '17px 0px 23px 40px',
  },
  submitBar: {
    flex: '0 0 auto',
    boxSizing: 'border-box' as const,
    padding: 16,
    gap: 10,
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  searchBar: {
    width: 350,
    marginTop: 4,
  },
  noneSelectedText: {
    fontSize: 13,
    lineHeight: 1.5,
    fontFamily: theme.fontFamily,
    color: theme.colors.gray[4],
    marginTop: 8,
  },
  chemicalStructureContainer: {
    height: `${TABLE_ROW_HEIGHT}px`,
    textAlign: 'center' as const,
  },
  chemicalStructureSvg: {
    width: '100%',
    height: '100%',
  },
}));
